/**
 * web/src/lib/dates.ts
 * Date helpers for every page that renders a server timestamp (Speaker Hub +
 * nudge timeline, accommodation pickup report). Server timestamps are
 * naive-UTC ISO strings ("2026-07-13T09:00:00") — no trailing "Z" — so
 * `new Date(iso)` would mis-parse them as LOCAL time. Two families here:
 *
 *   * `*Naive*` — for DATE-ONLY values (event start dates, date-armed
 *     scheduledFor rows stored at bare UTC midnight). Render the calendar day
 *     literally; TZ-converting would shift "Jul 28 00:00" to "Jul 27, 7 PM"
 *     for a US viewer and show the wrong date.
 *   * `*Utc*` — for REAL INSTANTS (nudge dueAt/sentAt, inbound-reply times,
 *     the pickup report's last-synced stamp). Parse as UTC, display in the
 *     VIEWER'S local timezone (2026-07-21: the nudge timeline showed "14:05"
 *     to a Central-time user at 9:05 AM; 2026-07-26: the pickup report's sync
 *     banner read "Jul 27, 12:57 AM" to a viewer whose clock said Jul 26).
 */

/** Render a naive-UTC ISO timestamp without TZ shifting: "Jul 17, 2026 · 14:30". */
export function formatNaiveIso(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const label = new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = iso.slice(11, 16);
  return time ? `${label} · ${time}` : label;
}

/**
 * Weekend-skipping date math on a naive ISO string — mirrors the server's
 * add_business_days so a client-added entry lands where a seeded one would.
 */
export function addBusinessDaysNaive(iso: string, n: number): string {
  const timePart = iso.length > 11 ? iso.slice(11) : '09:00:00';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(y || 2026, (m || 1) - 1, d || 1);
  let remaining = n;
  while (remaining > 0) {
    dt.setDate(dt.getDate() + 1);
    const dow = dt.getDay();
    if (dow !== 0 && dow !== 6) remaining -= 1;
  }
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${timePart}`;
}

/** Whole calendar days from today (local midnight) to a naive ISO date. */
export function daysUntilNaive(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return 0;
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** "in 3 days" / "today" / "2 days overdue" — the timeline's relative label. */
export function relativeDaysText(iso: string): string {
  const days = daysUntilNaive(iso);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${-days} day${days === -1 ? '' : 's'} overdue`;
}

// ─── Real-instant helpers: naive-UTC wire format ⇄ viewer-local display ───────

/** Parse a naive-UTC ISO string ("2026-07-28T14:05:00") into a real Date. */
export function parseUtcIso(iso: string): Date | null {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const hh = Number(iso.slice(11, 13) || 0);
  const mm = Number(iso.slice(14, 16) || 0);
  const ss = Number(iso.slice(17, 19) || 0);
  return new Date(Date.UTC(y, m - 1, d, hh, mm, ss));
}

/** Render a naive-UTC instant in the viewer's timezone: "Jul 28, 2026 · 9:05 AM". */
export function formatUtcIsoLocal(iso: string): string {
  const dt = parseUtcIso(iso);
  if (!dt) return iso;
  const label = dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${label} · ${time}`;
}

/**
 * Like `formatUtcIsoLocal`, plus the viewer's timezone abbreviation:
 * "Jul 26, 2026 · 7:57 PM CDT". For stamps a user might compare against an
 * external system (the pickup report's last-synced time vs. Cvent Passkey) —
 * naming the zone is what makes "is this current?" answerable rather than a
 * guess about whose clock the number belongs to.
 */
export function formatUtcIsoLocalWithZone(iso: string): string {
  const dt = parseUtcIso(iso);
  if (!dt) return iso;
  const label = dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = dt.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${label} · ${time}`;
}

/** Whole calendar days from today to a naive-UTC instant — both in LOCAL days. */
export function daysUntilUtc(iso: string): number {
  const dt = parseUtcIso(iso);
  if (!dt) return 0;
  const target = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** relativeDaysText for a real instant — relative to the viewer's local day. */
export function relativeDaysTextUtc(iso: string): string {
  const days = daysUntilUtc(iso);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${-days} day${days === -1 ? '' : 's'} overdue`;
}

/** Naive-UTC instant → the viewer-local "YYYY-MM-DDTHH:mm" a
 *  `<input type="datetime-local">` expects as its value. */
export function utcIsoToLocalInput(iso: string): string {
  const dt = parseUtcIso(iso);
  if (!dt) return iso.slice(0, 16);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

/** Viewer-local "YYYY-MM-DDTHH:mm" (datetime-local value) → the naive-UTC ISO
 *  the server stores. Inverse of `utcIsoToLocalInput`. */
export function localInputToUtcIso(value: string): string {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  const hh = Number(value.slice(11, 13) || 0);
  const mm = Number(value.slice(14, 16) || 0);
  // new Date(y, m, d, ...) interprets the parts in the viewer's timezone.
  return new Date(y, m - 1, d, hh, mm, 0).toISOString().slice(0, 19);
}

/**
 * Weekend-skipping date math on a naive-UTC INSTANT, done in the viewer's
 * LOCAL calendar (so "+5 business days" counts the weekdays the user sees and
 * preserves the local time-of-day across a DST boundary). Returns naive-UTC.
 */
export function addBusinessDaysUtc(iso: string, n: number): string {
  const dt = parseUtcIso(iso);
  if (!dt) return iso;
  let remaining = n;
  while (remaining > 0) {
    dt.setDate(dt.getDate() + 1); // local-calendar day step, local time kept
    const dow = dt.getDay();
    if (dow !== 0 && dow !== 6) remaining -= 1;
  }
  return dt.toISOString().slice(0, 19);
}

/** Why the thread stopped being chased — shown on auto-cancelled entries. */
export const NUDGE_CANCEL_LABEL: Record<string, string> = {
  confirmed: 'receipt confirmed',
  reply_detected: 'reply received',
  step_terminal: 'step moved on',
  nothing_outstanding: 'nothing left to chase',
  workflow_terminal: 'workflow ended',
  inbound_activity: 'held — they wrote first',
  manual: 'cancelled manually',
};
